import { NavMarker } from 'react-rhumb';
import { Table, SortTH } from 'foundations-components/transitional/components/Table';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ApiError from '../../../../components/Errors/ApiError';
import { useTeamMembersCertificationsByStatus } from '../../myTeamQueries';
import ACTableSkeleton from 'academy-common-lib/components/loading/ACTableSkeleton';
import UserAvatarLink from '../common/UserAvatarLink';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useCertificationsByTeamInProgressTableStore } from '../../store/myTeamTableStateStore';
import { useParams } from 'quartz-routing/react-router';
import { parseCertificationStatusFilter } from '../../utils/filterUtils';
import EmptyTable from '../common/EmptyTable';
import MyTeamTablePagination from '../common/TablePagination';
import { useTableSorting } from '../../hooks/tableHooks';
import CertificationLink from '../common/CertificationLink';
import { getOffsetPosition, getPaginatedPosition } from '../../utils/paginationUtils';
import ProgressDisplay from '../common/ProgressDisplay';
import { useTheme } from 'foundations-theming/utils';
import { useRef } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CertificationsByTeamInProgressTable = () => {
  const theme = useTheme();
  const {
    certificationStatus: certificationStatusParam
  } = useParams();
  const certificationsByTeamInProgressTableStore = useCertificationsByTeamInProgressTableStore();
  const {
    state: {
      search,
      teams,
      users,
      categories,
      tools,
      sortBy,
      sortOrder,
      page,
      pageSize
    },
    actions: {
      resetTableState,
      setSortBy,
      setSortOrder,
      setPage,
      setPageSize
    }
  } = certificationsByTeamInProgressTableStore;
  const {
    isNotTablet
  } = useMediaQuery();
  const totalResultsRef = useRef(0);
  const {
    handleSortChange,
    getSortDirectionForColumn
  } = useTableSorting({
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder
  });
  const offsetPosition = getOffsetPosition(page, pageSize);
  const {
    teamMembersCertifications,
    teamMembersCertificationsLoading,
    teamMembersCertificationsError
  } = useTeamMembersCertificationsByStatus({
    certificationStatus: parseCertificationStatusFilter(certificationStatusParam),
    teamIds: teams,
    userIds: users,
    categories,
    tools,
    pageLength: pageSize,
    teamMemberNameFilter: search,
    sortField: sortBy,
    sortOrder: sortOrder,
    offset: {
      offsetPosition
    }
  });
  if ((teamMembersCertifications === null || teamMembersCertifications === void 0 ? void 0 : teamMembersCertifications.results.length) === 0) {
    return /*#__PURE__*/_jsx(EmptyTable, {
      navMarkerName: "CERTIFICATIONS_BY_TEAM_TABLE_EMPTY",
      onResetClick: resetTableState
    });
  }
  if (teamMembersCertificationsError) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "CERTIFICATIONS_BY_TEAM_TABLE_API_ERROR",
      children: /*#__PURE__*/_jsx(ApiError, {
        error: {
          status: 500
        }
      })
    });
  }
  if (!teamMembersCertificationsLoading && teamMembersCertifications) {
    totalResultsRef.current = teamMembersCertifications.total;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [teamMembersCertificationsLoading ? /*#__PURE__*/_jsx(ACTableSkeleton, {
      columns: 3,
      condensed: true,
      rows: pageSize
    }) : teamMembersCertifications && /*#__PURE__*/_jsx(NavMarker, {
      name: "CERTIFICATIONS_BY_TEAM_TABLE_LOADED",
      children: /*#__PURE__*/_jsxs(Table, {
        condensed: true,
        "data-test-id": "in-progress-certifications-table",
        hover: true,
        responsive: true,
        children: [/*#__PURE__*/_jsxs("colgroup", {
          children: [/*#__PURE__*/_jsx("col", {
            style: {
              width: '25%',
              borderInlineEnd: `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}`
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '50%'
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '25%'
            }
          })]
        }), /*#__PURE__*/_jsx("thead", {
          children: /*#__PURE__*/_jsxs("tr", {
            children: [/*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('NAME'),
              sort: getSortDirectionForColumn('NAME'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.name"
              })
            }), /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('CERTIFICATION_NAME'),
              sort: getSortDirectionForColumn('CERTIFICATION_NAME'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.certification"
              })
            }), /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('PROGRESS'),
              sort: getSortDirectionForColumn('PROGRESS'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.progress"
              })
            })]
          })
        }), /*#__PURE__*/_jsx("tbody", {
          children: teamMembersCertifications.results.map((cert, index) => {
            const position = getPaginatedPosition(offsetPosition, index);
            return /*#__PURE__*/_jsxs("tr", {
              "data-test-id": "certifications-by-team-in-progress-table-data-row",
              children: [/*#__PURE__*/_jsx("td", {
                children: /*#__PURE__*/_jsx(UserAvatarLink, {
                  displayAvatar: isNotTablet,
                  position: position,
                  userEmail: cert.userEmail,
                  userId: cert.userId,
                  userName: cert.userName
                })
              }), /*#__PURE__*/_jsx("td", {
                children: /*#__PURE__*/_jsx(CertificationLink, {
                  certificationIconUrl: cert.certificationIconUrl,
                  certificationId: cert.certificationId,
                  certificationName: cert.certificationName,
                  position: position
                })
              }), /*#__PURE__*/_jsx("td", {
                children: /*#__PURE__*/_jsx(ProgressDisplay, {
                  progress: cert.progress
                })
              })]
            }, `${cert.userId}-${cert.certificationId}`);
          })
        })]
      })
    }), /*#__PURE__*/_jsx(MyTeamTablePagination, {
      onPageChange: setPage,
      onPageSizeChange: setPageSize,
      page: page,
      pageSize: pageSize,
      totalResults: totalResultsRef.current
    })]
  });
};
export default CertificationsByTeamInProgressTable;